import type { Translate } from '../i18n';
import { diffTokens, type Change, type DiffSeg } from '../lib/tokenDiff';

const DEL = 'rounded-sm bg-error/10 px-0.5 text-error line-through decoration-1';
const INS = 'rounded-sm bg-accent/[0.16] px-0.5 font-semibold text-accent-strong no-underline';

export function changeText(change: Change, t: Translate): string {
  switch (change.kind) {
    case 'values':
      return change.size === null
        ? t('fix.change.value', { from: change.from, to: change.to })
        : t('fix.change.size', { size: change.size, from: change.from, to: change.to });
    case 'removed':
      return t('fix.change.removed', { text: change.text });
    case 'added':
      return t('fix.change.added', { text: change.text });
    case 'replaced':
      return t('fix.change.replaced', { from: change.from, to: change.to });
  }
}

/** One-line summary of a line edit, e.g. « taille L : 86 → 84 ». */
export function ChangeSummary({ changes, t }: { changes: Change[]; t: Translate }) {
  if (changes.length === 0) return null;
  return (
    <p className="text-sm font-semibold text-fg tabular" data-testid="fix-summary">
      {changes.map((c) => changeText(c, t)).join(' ; ')}
    </p>
  );
}

/**
 * Source line edit, inline: unchanged text, removed tokens struck through, added tokens marked
 * (a multi-size group is one token). The line is pattern text: never translated.
 */
export function SourceEdit({ oldText, newText, patternLang }: { oldText: string; newText: string; patternLang?: string }) {
  return (
    <code
      className="verbatim block whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-fg"
      data-testid="fix-source-edit"
      data-verbatim
      lang={patternLang}
    >
      {diffTokens(oldText, newText).map((seg, i) =>
        seg.kind === 'same' ? (
          <span key={i}>{seg.text}</span>
        ) : seg.kind === 'removed' ? (
          <del key={i} className={DEL}>
            {seg.text}
          </del>
        ) : (
          <ins key={i} className={INS}>
            {seg.text}
          </ins>
        ),
      )}
    </code>
  );
}

function elide(text: string, where: 'start' | 'end' | 'middle'): string {
  const KEEP = 26;
  if (where === 'middle' ? text.length <= 2 * KEEP + 6 : text.length <= KEEP + 6) return text;
  const head = (s: string) => {
    const cut = s.slice(0, KEEP);
    const sp = cut.lastIndexOf(' ');
    return (sp > 8 ? cut.slice(0, sp + 1) : cut) + '… ';
  };
  const tail = (s: string) => {
    const cut = s.slice(-KEEP);
    const sp = cut.indexOf(' ');
    return ' …' + (sp >= 0 && sp < KEEP - 8 ? cut.slice(sp) : cut);
  };
  if (where === 'start') return tail(text).trimStart();
  if (where === 'end') return head(text).trimEnd();
  return head(text) + tail(text).trimStart();
}

function compact(segs: DiffSeg[]): DiffSeg[] {
  return segs.map((s, i) => {
    if (s.kind !== 'same') return s;
    const where = i === 0 ? 'start' : i === segs.length - 1 ? 'end' : 'middle';
    return { ...s, text: elide(s.text, where) };
  });
}

/** Canonical « avant / après » (the engine's English canonical form), when the fix is not a line edit. */
export function BeforeAfter({ before, after, t }: { before: string; after: string; t: Translate }) {
  const segs = compact(diffTokens(before, after));
  const line = (hide: 'added' | 'removed') =>
    segs
      .filter((s) => s.kind !== hide)
      .map((s, i) =>
        s.kind === 'same' ? (
          <span key={i}>{s.text}</span>
        ) : (
          <mark key={i} className={s.kind === 'removed' ? DEL : INS}>
            {s.text}
          </mark>
        ),
      );
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm leading-relaxed" data-testid="fix-before-after">
      <dt className="kicker select-none pt-0.5">{t('fix.before')}</dt>
      <dd className="whitespace-pre-wrap break-words text-muted" data-verbatim>
        {line('added')}
      </dd>
      <dt className="kicker select-none pt-0.5">{t('fix.after')}</dt>
      <dd className="whitespace-pre-wrap break-words text-fg" data-verbatim>
        {line('removed')}
      </dd>
    </dl>
  );
}
